import React, { useState, useEffect } from 'react';
import {
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Button,
  CircularProgress
} from '@mui/material';
import { diagnosticsSftpListDirectory } from '_services/ApiService';
import SecretField from '../SecretField';
import { SFTPBrowser } from './browser';

/**
 *
 * @param {{
 * username:string;
 * password:string | {type:string,value:string,iv:string};
 * host:string;
 * port:number;
 * directory:string;
 * }} props
 */
export const TestSftp = (
  {
    is1PasswordItem,
    initialOnePasswordPath,
    initialHost,
    initialPort,
    initialUsername,
    initialPassword,
    initialDirectory,
    initialPassphrase,
    initialPrivateKey
  },
  props
) => {
  const [onePasswordPath, setOnePasswordPath] = useState(initialOnePasswordPath);
  const [host, setHost] = useState(initialHost);
  const [port, setPort] = useState(initialPort);
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState(initialPassword);
  const [directory, setDirectory] = useState(initialDirectory || '/');
  const [passphrase, setPassphrase] = useState(initialPassphrase);
  const [privateKey, setPrivateKey] = useState(initialPrivateKey);

  const [connectionStatus, setConnectionStatus] =
    React.useState('disconnected');

  useEffect(
    () => setConnectionStatus('disconnected'),
    is1PasswordItem
      ? [onePasswordPath, directory]
      : [host, port, username, password, directory, passphrase, privateKey]
  );

  const toggleConnect = async () => {
    if (connectionStatus === 'connected') {
      return setConnectionStatus('disconnected');
    }
    if (connectionStatus === 'disconnected') {
      setConnectionStatus('connecting');
      try {
        const credentials = is1PasswordItem
          ? {
              type: '1password_item',
              '1password_item_path': onePasswordPath
            }
          : { host, port, username, password, passphrase, privateKey };
        
        const result = await diagnosticsSftpListDirectory({
          credentials,
          directory
        });
        if (result.type === 'error') {
          throw new Error(result.message);
        } else {
          setConnectionStatus('connected');
        }
      } catch (e) {
        alert(e.message);
        setConnectionStatus('disconnected');
      }
      return;
    }
  };

  const formStyle =
    connectionStatus === 'connected'
      ? {
          maxHeight: '0px',
          overflow: 'hidden',
          transition: 'max-height 0.3s, opacity 0.4s',
          opacity: '0%'
        }
      : {
          maxHeight: '100%',
          overflow: 'hidden',
          transition: 'max-height: 0.3s'
        };

  return (
    <>
      <CardHeader title="Test sftp" />
      <Divider />
      <CardContent style={formStyle}>
        {is1PasswordItem ? (
          <Grid container spacing={3}>
            <Grid item md={12} xs={12}>
              <TextField
                fullWidth
                label="1Password Item Path"
                placeholder="Client credentials/Empower 401k SFTP"
                name="onePasswordPath"
                onChange={(e) => setOnePasswordPath(e.target.value)}
                required
                value={onePasswordPath}
                variant="outlined"
              />
            </Grid>
            <Grid item md={12} xs={12}>
              <TextField
                fullWidth
                label="Directory"
                name="directory"
                onChange={(e) => setDirectory(e.target.value)}
                required
                value={directory}
                variant="outlined"
              />
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  placeholder='username'
                  name="username"
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  value={username}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  name="directory"
                  onChange={(e) => setDirectory(e.target.value)}
                  required
                  value={directory}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  placeholder="host"
                  name="host"
                  onChange={(e) => setHost(e.target.value)}
                  required
                  value={host}
                  variant="outlined"
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  placeholder="port"
                  name="port"
                  onChange={(e) => setPort(e.target.value)}
                  required
                  value={port}
                  variant="outlined"
                />
              </Grid>
            </Grid>
            <Grid container spacing={3}>
              <Grid item md={6} xs={6}>
                <SecretField
                  label="password"
                  secret={password}
                  setState={setPassword}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item md={6} xs={6}>
                <SecretField
                  label="private key"
                  secret={privateKey}
                  setState={setPrivateKey}
                  multiline
                />
              </Grid>
              <Grid item md={6} xs={6}>
                <SecretField
                  label="passphrase"
                  secret={passphrase}
                  setState={setPassphrase}
                />
              </Grid>
            </Grid>
          </>
        )}
      </CardContent>
      <CardContent>
        <Button
          color="primary"
          onClick={toggleConnect}
          variant={
            connectionStatus === 'disconnected' ? 'contained' : 'outline'
          }
        >
          {connectionStatus === 'disconnected' ? (
            'Connect'
          ) : connectionStatus === 'connecting' ? (
            <>
              <CircularProgress />
              Connecting
            </>
          ) : (
            'Connected (click to disconnect)'
          )}
        </Button>
      </CardContent>
      <Divider />
      {connectionStatus === 'connected' && (
        <CardContent>
          <SFTPBrowser
            credentials={is1PasswordItem
              ? { type: '1password_item', '1password_item_path': onePasswordPath }
              : { host, port, username, password, privateKey, passphrase }
            }
            initialDirectory={directory}
          />
        </CardContent>
      )}
    </>
  );
};
